/* eslint-disable react/prop-types */
import React from 'react';
import { Box, Flex, Text } from 'rebass';
import dayjs from 'dayjs';
import { Trans } from '@lingui/macro';
import { Avatar, Typography, SelectBox } from '@oca/ui';
import { Screen } from '@features/common';
import { SkeletonRenderer } from '@lib/skeleton-loader';
import { getFullName } from '@lib/help-fns';
import {
  ClockDash,
  Loading,
  Work,
  CalendarBold,
  UsersChecked,
} from '@oca/icons';
import UserBox from '@oca/ui/molecules/user-box';
import PriorityBadge from '@oca/ui/molecules/prio-badge';
import { statuses } from '../datas';

const assigneeAvailableStatuses = statuses.filter(
  status => status.value !== 'done',
);

export const ViewMeta = ({
  createdAt,
  creator,
  dueDate,
  isLoading,
  isOwner,
  onStatusChange,
  project,
  priority,
  status,
}) => {
  return (
    <Flex py={20} flexDirection="column" style={{ gap: 20 }}>
      <SkeletonRenderer
        width={140}
        height={14}
        loading={isLoading}
        render={() => (
          <Descriptor
            icon={<ClockDash size={20} />}
            field={<Trans>Status</Trans>}
            render={() =>
              status && (
                <Box mb="-4px">
                  <Dropdown
                    options={isOwner ? statuses : assigneeAvailableStatuses}
                    value={statuses.find(item => item.value === status)}
                    onChange={item => onStatusChange(item.value)}
                  />
                </Box>
              )
            }
          />
        )}
      />

      <SkeletonRenderer
        width={140}
        height={14}
        loading={isLoading}
        render={() => (
          <Descriptor
            icon={<Loading size={20} />}
            field={<Trans>Priority</Trans>}
            render={() => (
              <PriorityBadge level={priority || 'low'} lineHeight={1}>
                {priority || 'N/A'}
              </PriorityBadge>
            )}
          />
        )}
      />

      <SkeletonRenderer
        width={180}
        height={14}
        loading={isLoading}
        render={() => (
          <Descriptor
            icon={<Work size={20} />}
            field={<Trans>Project</Trans>}
            render={() => (
              <Typography fontWeight={500} color="textSecondary" noWrap>
                {project ? project.name : '---'}
              </Typography>
            )}
          />
        )}
      />

      <SkeletonRenderer
        width={120}
        height={14}
        loading={isLoading}
        render={() => (
          <Descriptor
            icon={<CalendarBold size={20} />}
            field={<Trans>Due date</Trans>}
            render={() => (
              <>
                <Typography
                  fontWeight={500}
                  color="textSecondary"
                  lineHeight={1}
                  mr={2}
                  noWrap
                >
                  <ResponsiveDate
                    date={dueDate}
                    compactFormat="L LT"
                    standardFormat="L LT"
                  />
                </Typography>
                <Box mb={-1}>
                  <Overdue time={dueDate} status={status} />
                </Box>
              </>
            )}
          />
        )}
      />

      <SkeletonRenderer
        width={140}
        height={14}
        loading={isLoading}
        render={() => (
          <Descriptor
            icon={<CalendarBold size={20} />}
            field={<Trans>Task Created</Trans>}
            render={() => (
              <Typography
                fontWeight={500}
                color="textSecondary"
                lineHeight={1}
                noWrap
              >
                <ResponsiveDate
                  date={createdAt}
                  compactFormat="L LT"
                  standardFormat="L LT"
                />
              </Typography>
            )}
          />
        )}
      />

      <SkeletonRenderer
        width={180}
        height={14}
        loading={isLoading}
        render={() => (
          <Descriptor
            icon={<UsersChecked size={20} />}
            field={<Trans>Creator</Trans>}
            render={() => (
              <UserBox>
                <Avatar
                  src={creator ? creator.avatar : undefined}
                  size={24}
                  mr={2}
                >
                  <UsersChecked />
                </Avatar>
                <Typography color="textSecondary" noWrap fontWeight={500}>
                  {creator ? getFullName(creator, { middleKey: null }) : '---'}
                </Typography>
              </UserBox>
            )}
          />
        )}
      />
    </Flex>
  );
};

const Descriptor = ({ field, render, icon, ...rest }) => (
  <Flex alignItems="center" flexWrap="wrap" {...rest}>
    <Flex alignItems="center" width={['auto', '300px']} mr={[3, 0]}>
      <Flex alignItems="center" mr={3}>
        {icon}
        <Typography color="textSecondary" fontSize={12} lineHeight={1} ml={2}>
          {field}
        </Typography>
      </Flex>
    </Flex>

    <Box flex="1">{render()}</Box>
  </Flex>
);

function ResponsiveDate({ date, compactFormat, standardFormat }) {
  if (date) {
    return (
      <Screen>
        {({ xsmall }) =>
          dayjs(new Date(date)).format(xsmall ? compactFormat : standardFormat)
        }
      </Screen>
    );
  }
  return '---';
}

function Overdue({ status, time }) {
  return (
    dayjs().isAfter(new Date(time)) &&
    ['todo', 'in_progress'].includes(status) && (
      <Text
        padding={1}
        fontSize={12}
        color="white"
        bg="error"
        sx={{ borderRadius: 4 }}
      >
        <Trans>overdue</Trans>
      </Text>
    )
  );
}

function Dropdown(props) {
  return (
    <SelectBox
      {...props}
      styles={{
        dropdownIndicator: base => ({ ...base, padding: 0 }),
        valueContainer: base => ({ ...base, padding: '0 8px' }),
        input: base => ({
          ...base,
          fontSize: 14,
          paddingTop: 0,
          paddingBottom: 0,
        }),
        control: base => ({
          ...base,
          backgroundColor: '#f6f8fc',
          width: 110,
          height: 20,
          minHeight: 30,
          borderRadius: 8,
        }),
        indicatorSeparator: () => ({ visibility: 'hidden' }),
      }}
      menuPortalTarget={document.body}
      getOptionValue={option => option.value}
      getOptionLabel={option => (
        <Typography color="inherit" lineHeight="15px">
          {getStatusLabel(option.value)}
        </Typography>
      )}
    />
  );
}

function getStatusLabel(status) {
  const found = statuses.find(({ value }) => value === status);
  return found ? found.label : 'N/A';
}
